import React, { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ChevronRightIcon,
  CurrencyDollarIcon,
  ClipboardListIcon,
  XIcon,
  ArrowLeftIcon,
  FolderIcon,
} from "@heroicons/react/outline";
import { stagesAPI } from "../api/stages";
import { projectsAPI } from "../api/projects";
import LoadingSpinner from "../components/common/LoadingSpinner";
import StageForm from "../components/stages/StageForm";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";

const StagesPage = () => {
  const { projectId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingStage, setEditingStage] = useState(null);
  const [selectedStage, setSelectedStage] = useState(null);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [showTasksModal, setShowTasksModal] = useState(false);
  const queryClient = useQueryClient();

  const isAllStages = !projectId;

  // Fetch all stages if no projectId
  const { data: allStagesData, isLoading: allStagesLoading } = useQuery({
    queryKey: ["all-stages"],
    queryFn: () => stagesAPI.getAll(),
    enabled: isAllStages,
  });

  // Fetch project details
  const { data: projectData, isLoading: projectLoading } = useQuery({
    queryKey: ["project", projectId],
    queryFn: () => projectsAPI.getById(projectId),
    enabled: !!projectId,
  });

  // Fetch stages for specific project
  const { data: stagesData, isLoading: stagesLoading } = useQuery({
    queryKey: ["project-stages", projectId],
    queryFn: () => projectsAPI.getStages(projectId),
    enabled: !!projectId,
  });

  // Fetch stage tasks
  const { data: stageTasksData, isLoading: stageTasksLoading } = useQuery({
    queryKey: ["stage-tasks", selectedStage?.id],
    queryFn: () => stagesAPI.getTasks(selectedStage?.id),
    enabled: !!selectedStage?.id && showTasksModal,
  });

  // Fetch stage budget
  const { data: stageBudgetData, isLoading: stageBudgetLoading } = useQuery({
    queryKey: ["stage-budget", selectedStage?.id],
    queryFn: () => stagesAPI.getBudget(selectedStage?.id),
    enabled: !!selectedStage?.id && showBudgetModal,
  });

  // Update stage mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => stagesAPI.update(id, data),
    onSuccess: () => {
      if (projectId) {
        queryClient.invalidateQueries(["project-stages", projectId]);
      } else {
        queryClient.invalidateQueries(["all-stages"]);
      }
      toast.success("Stage updated successfully");
    },
    onError: () => toast.error("Failed to update stage"),
  });

  // Delete stage mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => stagesAPI.delete(id),
    onSuccess: () => {
      if (projectId) {
        queryClient.invalidateQueries(["project-stages", projectId]);
      } else {
        queryClient.invalidateQueries(["all-stages"]);
      }
      toast.success("Stage deleted successfully");
    },
    onError: () => toast.error("Failed to delete stage"),
  });

  // Complete stage mutation
  const completeMutation = useMutation({
    mutationFn: (id) => stagesAPI.complete(id),
    onSuccess: () => {
      if (projectId) {
        queryClient.invalidateQueries(["project-stages", projectId]);
      } else {
        queryClient.invalidateQueries(["all-stages"]);
      }
      toast.success("Stage completed successfully");
    },
    onError: () => toast.error("Failed to complete stage"),
  });

  // Determine which data to show
  const project = projectData?.data;
  const stages = projectId ? stagesData?.data || [] : allStagesData?.data || [];
  const isLoading = projectId
    ? projectLoading || stagesLoading
    : allStagesLoading;

  // Group stages by project (for "All Stages" view)
  const groupStagesByProject = (stagesList) => {
    const grouped = {};
    stagesList.forEach((stage) => {
      const projectIdKey = stage.project_id;
      if (!grouped[projectIdKey]) {
        grouped[projectIdKey] = {
          projectId: projectIdKey,
          projectName: stage.project_name || `Project ${projectIdKey}`,
          stages: [],
        };
      }
      grouped[projectIdKey].stages.push(stage);
    });
    return Object.values(grouped);
  };

  const groupedStages = isAllStages ? groupStagesByProject(stages) : [];

  const getStatusColor = (status) => {
    const colors = {
      pending: "badge-warning",
      in_progress: "badge-info",
      completed: "badge-success",
      blocked: "badge-danger",
    };
    return colors[status] || "badge-info";
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: <ClockIcon className="h-4 w-4" />,
      in_progress: <ClockIcon className="h-4 w-4 animate-pulse" />,
      completed: <CheckCircleIcon className="h-4 w-4" />,
      blocked: <XCircleIcon className="h-4 w-4" />,
    };
    return icons[status] || icons.pending;
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      pending:
        "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
      in_progress:
        "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
      completed:
        "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
      blocked: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
    };
    return classes[status] || classes.pending;
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this stage?")) {
      deleteMutation.mutate(id);
    }
  };

  const handleComplete = (id) => {
    if (window.confirm("Mark this stage as completed?")) {
      completeMutation.mutate(id);
    }
  };

  const handleUpdateStatus = (id, status) => {
    updateMutation.mutate({ id, data: { status } });
  };

  // Check if user can manage stages
  const canManage =
    user?.role === "super_admin" ||
    user?.role === "project_manager" ||
    user?.role === "sub_manager";

  if (isLoading) return <LoadingSpinner />;

  // Render stage card
  const renderStageCard = (stage, index) => (
    <motion.div
      key={stage.id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="card hover:shadow-lg transition-all duration-300"
    >
      <div className="flex items-start justify-between">
        {/* LEFT - Stage Info */}
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-lg font-semibold text-slate-900 dark:text-white">
              #{stage.stage_order || 0}
            </span>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              {stage.stage_name}
            </h3>
            <span
              className={`badge ${getStatusBadgeClass(stage.status)} flex items-center gap-1`}
            >
              {getStatusIcon(stage.status)}
              {stage.status || "Pending"}
            </span>
          </div>

          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
            {stage.description || "No description"}
          </p>

          {/* Project Link (only in "All Stages" view) */}
          {isAllStages && stage.project_id && (
            <Link
              to={`/projects/${stage.project_id}`}
              className="inline-flex items-center gap-1 text-xs text-primary-500 hover:text-primary-600 mt-1"
            >
              <FolderIcon className="h-3 w-3" />
              View Project →
            </Link>
          )}

          {/* Cost and Date Info */}
          <div className="flex items-center gap-4 mt-3 text-sm text-slate-500 dark:text-slate-400 flex-wrap">
            <span className="flex items-center gap-1">
              💰 ${stage.estimated_cost?.toLocaleString() || "0"}
            </span>
            <span className="flex items-center gap-1">
              💵 ${stage.actual_cost?.toLocaleString() || "0"}
            </span>
            {stage.start_date && (
              <span className="flex items-center gap-1">
                📅 {new Date(stage.start_date).toLocaleDateString()}
              </span>
            )}
            {stage.end_date && (
              <span className="flex items-center gap-1">
                📅 {new Date(stage.end_date).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>

        {/* RIGHT - Actions */}
        <div className="flex items-center gap-1 flex-wrap ml-4">
          {/* View Tasks Button */}
          <button
            onClick={() => {
              setSelectedStage(stage);
              setShowTasksModal(true);
            }}
            className="p-2 rounded-lg text-slate-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
            title="View Tasks"
          >
            <ClipboardListIcon className="h-5 w-5" />
          </button>

          {/* View Budget Button */}
          <button
            onClick={() => {
              setSelectedStage(stage);
              setShowBudgetModal(true);
            }}
            className="p-2 rounded-lg text-slate-400 hover:text-green-500 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
            title="View Budget"
          >
            <CurrencyDollarIcon className="h-5 w-5" />
          </button>

          {/* Status Update Dropdown - Only for managers */}
          {canManage && (
            <select
              value={stage.status || "pending"}
              onChange={(e) => handleUpdateStatus(stage.id, e.target.value)}
              className="text-xs px-2 py-1 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-1 focus:ring-primary-500"
            >
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="blocked">Blocked</option>
            </select>
          )}

          {/* Complete Button */}
          {canManage && stage.status !== "completed" && (
            <button
              onClick={() => handleComplete(stage.id)}
              className="p-2 rounded-lg text-green-500 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
              title="Complete Stage"
            >
              <CheckCircleIcon className="h-5 w-5" />
            </button>
          )}

          {/* Edit Button */}
          {canManage && (
            <button
              onClick={() => setEditingStage(stage)}
              className="p-2 rounded-lg text-slate-400 hover:text-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
              title="Edit Stage"
            >
              <PencilIcon className="h-5 w-5" />
            </button>
          )}

          {/* Delete Button - Only Super Admin */}
          {user?.role === "super_admin" && (
            <button
              onClick={() => handleDelete(stage.id)}
              className="p-2 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              title="Delete Stage"
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mt-3">
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-600 dark:text-slate-400">Completion</span>
          <span className="text-slate-900 dark:text-white font-medium">
            {stage.completion_percentage || 0}%
          </span>
        </div>
        <div className="mt-1 w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${
              (stage.completion_percentage || 0) >= 100
                ? "bg-green-500"
                : (stage.completion_percentage || 0) >= 50
                  ? "bg-primary-500"
                  : "bg-yellow-500"
            }`}
            style={{ width: `${stage.completion_percentage || 0}%` }}
          />
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          {projectId ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
              <Link to="/projects" className="hover:text-primary-500">
                Projects
              </Link>
              <ChevronRightIcon className="h-4 w-4" />
              <Link
                to={`/projects/${projectId}`}
                className="hover:text-primary-500"
              >
                {project?.name || "Project"}
              </Link>
              <ChevronRightIcon className="h-4 w-4" />
              <span className="text-slate-900 dark:text-white">Stages</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
              <Link to="/dashboard" className="hover:text-primary-500">
                Dashboard
              </Link>
              <ChevronRightIcon className="h-4 w-4" />
              <span className="text-slate-900 dark:text-white">All Stages</span>
            </div>
          )}
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mt-1">
            {projectId ? `Stages - ${project?.name || ""}` : "All Stages"}
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            {projectId
              ? `Manage stages for ${project?.name}`
              : "View all stages grouped by project"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {projectId && (
            <Link
              to={`/projects/${projectId}`}
              className="btn-secondary flex items-center gap-2"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Back to Project
            </Link>
          )}
          {canManage && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary flex items-center gap-2"
            >
              <PlusIcon className="h-5 w-5" />
              Add Stage
            </button>
          )}
        </div>
      </div>

      {/* Stages List */}
      {stages.length === 0 ? (
        <div className="text-center py-12">
          <ClockIcon className="h-16 w-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
            No stages found
          </h3>
          <p className="text-slate-600 dark:text-slate-400">
            {projectId
              ? "Create your first stage to organize your project"
              : "No stages have been created yet"}
          </p>
          {canManage && projectId && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary mt-4 inline-flex items-center gap-2"
            >
              <PlusIcon className="h-5 w-5" />
              Add Stage
            </button>
          )}
        </div>
      ) : isAllStages ? (
        // GROUPED VIEW - All Stages
        <div className="space-y-8">
          {groupedStages.map((group) => (
            <div key={group.projectId} className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <FolderIcon className="h-5 w-5 text-primary-500" />
                  <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                    {group.projectName}
                  </h2>
                  <span className="text-sm text-slate-400">
                    ({group.stages.length} stages)
                  </span>
                </div>
                <Link
                  to={`/projects/${group.projectId}`}
                  className="text-xs text-primary-500 hover:text-primary-600"
                >
                  View Project →
                </Link>
              </div>
              <div className="space-y-4 pl-4 border-l-2 border-primary-200 dark:border-primary-800">
                {group.stages.map((stage, index) =>
                  renderStageCard(stage, index),
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        // SINGLE PROJECT VIEW
        <div className="space-y-4">
          {stages.map((stage, index) => renderStageCard(stage, index))}
        </div>
      )}

      {/* ============================================ */}
      {/* MODALS */}
      {/* ============================================ */}

      {/* Create Stage Modal */}
      {showCreateModal && (
        <StageForm
          projectId={projectId ? parseInt(projectId) : null}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            if (projectId) {
              queryClient.invalidateQueries(["project-stages", projectId]);
            } else {
              queryClient.invalidateQueries(["all-stages"]);
            }
            setShowCreateModal(false);
          }}
        />
      )}

      {/* Edit Stage Modal */}
      {editingStage && (
        <StageForm
          projectId={editingStage.project_id}
          stage={editingStage}
          onClose={() => setEditingStage(null)}
          onSuccess={() => {
            if (projectId) {
              queryClient.invalidateQueries(["project-stages", projectId]);
            } else {
              queryClient.invalidateQueries(["all-stages"]);
            }
            setEditingStage(null);
          }}
        />
      )}

      {/* Stage Tasks Modal */}
      {showTasksModal && selectedStage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                  Tasks for {selectedStage.stage_name}
                </h2>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {stageTasksData?.data?.length || 0} tasks in this stage
                </p>
              </div>
              <button
                onClick={() => {
                  setShowTasksModal(false);
                  setSelectedStage(null);
                }}
                className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                <XIcon className="h-5 w-5 text-slate-500" />
              </button>
            </div>
            <div className="p-6">
              {stageTasksLoading ? (
                <LoadingSpinner />
              ) : (stageTasksData?.data || []).length === 0 ? (
                <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                  No tasks in this stage
                </div>
              ) : (
                <div className="space-y-3">
                  {(stageTasksData?.data || []).map((task) => (
                    <div
                      key={task.id}
                      className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700/30 rounded-lg"
                    >
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white">
                          {task.title}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          {task.status || "Todo"} • {task.priority || "Medium"}
                        </p>
                      </div>
                      <span
                        className={`badge ${task.status === "done" ? "badge-success" : "badge-warning"}`}
                      >
                        {task.status || "Todo"}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}

      {/* Stage Budget Modal */}
      {showBudgetModal && selectedStage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl max-w-lg w-full max-h-[80vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                  Budget for {selectedStage.stage_name}
                </h2>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Cost breakdown for this stage
                </p>
              </div>
              <button
                onClick={() => {
                  setShowBudgetModal(false);
                  setSelectedStage(null);
                }}
                className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                <XIcon className="h-5 w-5 text-slate-500" />
              </button>
            </div>
            <div className="p-6">
              {stageBudgetLoading ? (
                <LoadingSpinner />
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-slate-50 dark:bg-slate-700/30 rounded-lg text-center">
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        Estimated Cost
                      </p>
                      <p className="text-2xl font-bold text-slate-900 dark:text-white">
                        $
                        {stageBudgetData?.data?.estimated_cost?.toLocaleString() ||
                          "0"}
                      </p>
                    </div>
                    <div className="p-4 bg-slate-50 dark:bg-slate-700/30 rounded-lg text-center">
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        Actual Cost
                      </p>
                      <p className="text-2xl font-bold text-slate-900 dark:text-white">
                        $
                        {stageBudgetData?.data?.actual_cost?.toLocaleString() ||
                          "0"}
                      </p>
                    </div>
                  </div>
                  {(stageBudgetData?.data?.task_estimated_cost !== undefined ||
                    stageBudgetData?.data?.task_actual_cost !== undefined) && (
                    <div className="border-t border-slate-200 dark:border-slate-700 pt-4 mt-2">
                      <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">
                        Task Costs
                      </p>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-3 bg-slate-50 dark:bg-slate-700/30 rounded-lg text-center">
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            Estimated
                          </p>
                          <p className="text-lg font-semibold text-slate-900 dark:text-white">
                            $
                            {stageBudgetData?.data?.task_estimated_cost?.toLocaleString() ||
                              "0"}
                          </p>
                        </div>
                        <div className="p-3 bg-slate-50 dark:bg-slate-700/30 rounded-lg text-center">
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            Actual
                          </p>
                          <p className="text-lg font-semibold text-slate-900 dark:text-white">
                            $
                            {stageBudgetData?.data?.task_actual_cost?.toLocaleString() ||
                              "0"}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default StagesPage;
